import { MetaCreativeKind } from '../../../../../models/meta-template/preview-interfaces/meta-preview-data.interface';

/**
 * Facebook Reels tints its call-to-action button with a dark shade of the
 * creative's own colour (#556426 olive on the green reference creative). This
 * approximates it: the average colour of the creative's lower half (the first
 * frame of a video), kept at its hue but set to Meta's dark lightness.
 *
 * Reading pixels needs a CORS-readable image or video. Blob URLs (a fresh
 * upload) always are; a stored file whose host sends no CORS headers is not,
 * and then the neutral fallback is used.
 */
export class MetaImageTone {
  static readonly FALLBACK = '#3e4042';

  private static readonly cache = new Map<string, Promise<string>>();

  static measure(src: string, kind: MetaCreativeKind = 'image'): Promise<string> {
    if (!src) {
      return Promise.resolve(this.FALLBACK);
    }
    let tone = this.cache.get(src);
    if (!tone) {
      tone = kind === 'video' ? this.readVideo(src) : this.read(src);
      this.cache.set(src, tone);
    }
    return tone;
  }

  private static read(src: string): Promise<string> {
    return new Promise((resolve) => {
      const image = new Image();
      image.crossOrigin = 'anonymous';
      image.onload = () => resolve(this.sample(image));
      image.onerror = () => resolve(this.FALLBACK);
      image.src = src;
    });
  }

  private static sample(source: CanvasImageSource): string {
    try {
      const canvas = document.createElement('canvas');
      canvas.width = 16;
      canvas.height = 16;
      const context = canvas.getContext('2d');
      if (!context) {
        return this.FALLBACK;
      }
      context.drawImage(source, 0, 0, 16, 16);
      const pixels = context.getImageData(0, 8, 16, 8).data;
      let r = 0;
      let g = 0;
      let b = 0;
      for (let i = 0; i < pixels.length; i += 4) {
        r += pixels[i];
        g += pixels[i + 1];
        b += pixels[i + 2];
      }
      const count = pixels.length / 4;
      return this.darken(r / count, g / count, b / count);
    } catch {
      return this.FALLBACK; // tainted canvas: the host sent no CORS headers
    }
  }

  /** The first frame of a video. A GIF uploaded to a video ad cannot play, so it is read as an image. */
  private static readVideo(src: string): Promise<string> {
    return new Promise((resolve) => {
      const video = document.createElement('video');
      video.crossOrigin = 'anonymous';
      video.muted = true;
      video.preload = 'auto';
      video.onloadeddata = () => {
        resolve(this.sample(video));
        // Let go of the decoder now the frame is read.
        video.removeAttribute('src');
        video.load();
      };
      video.onerror = () => resolve(this.read(src));
      video.src = src;
    });
  }

  /** Keep the hue, set Meta's lightness (27%) and a saturation between 35% and 60%. */
  private static darken(r: number, g: number, b: number): string {
    const [h, s] = this.toHsl(r / 255, g / 255, b / 255);
    const saturation = Math.min(0.6, Math.max(0.35, s));
    return `hsl(${Math.round(h * 360)}, ${Math.round(saturation * 100)}%, 27%)`;
  }

  private static toHsl(r: number, g: number, b: number): [number, number] {
    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    const l = (max + min) / 2;
    if (max === min) {
      return [0, 0];
    }
    const d = max - min;
    const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    let h: number;
    if (max === r) {
      h = (g - b) / d + (g < b ? 6 : 0);
    } else if (max === g) {
      h = (b - r) / d + 2;
    } else {
      h = (r - g) / d + 4;
    }
    return [h / 6, s];
  }
}
